import * as t from "io-ts";
import { X25519PublicKey, X25519SecretKey } from "sodium-plus";
import { UnreachableVariantError } from "../../unreachable-variant-error";
import getSodium from "../../sodium";
import { PlainBrand } from "../brands/PlainBrand";

export const PlainString = t.brand(
    t.string,
    (n): n is t.Branded<string, PlainBrand> => true,
    "Plain"
);
export type PlainString = t.TypeOf<typeof PlainString>
export type PlainStringOutput = typeof PlainString._O

export type PlainValue = PlainString | number | boolean | PlainArray | PlainObject | null;
export type PlainValueOutput = string | number | boolean | PlainArrayOutput | PlainObjectOutput | null;
export const PlainValue: t.Type<PlainValue, PlainValueOutput> = t.recursion("PlainValue", () =>
    t.union([PlainString, t.number, t.boolean, t.null, PlainArray, PlainObject]));

export interface PlainObject {
    readonly [key: string]: PlainValue;
}
export interface PlainObjectOutput {
    readonly [key: string]: PlainValueOutput
}
export const PlainObject: t.Type<PlainObject, PlainObjectOutput> = t.recursion("PlainObject", () =>
    t.readonly(t.record(t.string, PlainValue)));

export type PlainArray = ReadonlyArray<PlainValue>
export type PlainArrayOutput = ReadonlyArray<PlainValueOutput>
export const PlainArray: t.Type<PlainArray, PlainArrayOutput> = t.recursion("PlainArray", () =>
    t.readonlyArray(PlainValue));


interface EncryptedBrand {
    readonly Encrypted: unique symbol;
}

export const EncryptedString = t.brand(
    t.string,
    (n): n is t.Branded<string, EncryptedBrand> => true,
    "Encrypted"
);
export type EncryptedString = t.TypeOf<typeof EncryptedString>
export type EncryptedStringOutput = typeof EncryptedString._O

export type EncryptedValue = EncryptedString | number | boolean | EncryptedArray | EncryptedObject | null;
export type EncryptedValueOutput = string | number | boolean | EncryptedArrayOutput | EncryptedObjectOutput | null;
export const EncryptedValue: t.Type<EncryptedValue, EncryptedValueOutput> = t.recursion("EncryptedValue", () =>
    t.union([EncryptedString, t.number, t.boolean, t.null, EncryptedArray, EncryptedObject]));

export interface EncryptedObject {
    readonly [key: string]: EncryptedValue;
}
export interface EncryptedObjectOutput {
    readonly [key: string]: EncryptedValueOutput
}
export const EncryptedObject: t.Type<EncryptedObject, EncryptedObjectOutput> = t.recursion("EncryptedObject", () =>
    t.readonly(t.record(t.string, EncryptedValue)));

export type EncryptedArray = ReadonlyArray<EncryptedValue>
export type EncryptedArrayOutput = ReadonlyArray<EncryptedValueOutput>
export const EncryptedArray: t.Type<EncryptedArray, EncryptedArrayOutput> = t.recursion("EncryptedArray", () =>
    t.readonlyArray(EncryptedValue));

async function encryptString(publicKey: X25519PublicKey, input: PlainString): Promise<EncryptedString> {
    const sodium = await getSodium();
    const buffer = Buffer.from(input);
    const encryptedKeyData = await sodium.crypto_box_seal(buffer, publicKey);
    return encryptedKeyData.toString("base64") as EncryptedString;
}

async function encryptObject(publicKey: X25519PublicKey, input: PlainObject): Promise<EncryptedObject> {
    const encryptedObject: { [key: string]: EncryptedValue } = {};
    for (const key in input) {
        const value = input[key] as typeof input[typeof key];
        encryptedObject[key] = await encrypt(publicKey, value);
    }
    return encryptedObject;
}

function encryptArray(publicKey: X25519PublicKey, input: PlainArray): Promise<EncryptedArray> {
    return Promise.all(input.map(x => encrypt(publicKey, x)));
}

export async function encrypt(publicKey: X25519PublicKey, input: PlainString): Promise<EncryptedString>
export async function encrypt(publicKey: X25519PublicKey, input: PlainObject): Promise<EncryptedObject>
export async function encrypt(publicKey: X25519PublicKey, input: PlainArray): Promise<EncryptedArray>
export async function encrypt(publicKey: X25519PublicKey, input: PlainValue): Promise<EncryptedValue>
export async function encrypt(publicKey: X25519PublicKey, input: PlainValue): Promise<EncryptedValue> {
    switch (typeof input) {
        case "bigint":
        case "boolean":
        case "number":
            return input;

        case "string":
            return encryptString(publicKey, input);

        case "object":
            if (!input) {
                return null;
            }

            if (Array.isArray(input)) {
                return encryptArray(publicKey, input);
            }

            return encryptObject(publicKey, input);

        case "function":
        case "symbol":
        case "undefined":
            throw new UnreachableVariantError(input);
    }
}


async function decryptString(publicKey: X25519PublicKey, secretKey: X25519SecretKey, input: EncryptedString): Promise<PlainString> {
    const sodium = await getSodium();
    const encrypted =
        // Wir speichern jetzt alles als base64. Vorläufig können auch die Daten mit ArrayBuffer-Kodierung entschlüsselt werden.
        input.startsWith("{\"type\":\"Buffer\",\"data\":") ? Buffer.from(JSON.parse(input) as ArrayBuffer)
            : Buffer.from(input, "base64");

    const decryptedKeyData = await sodium.crypto_box_seal_open(
        encrypted,
        publicKey,
        secretKey
    );
    return decryptedKeyData.toString("utf-8") as PlainString;
}

async function decryptObject(publicKey: X25519PublicKey, secretKey: X25519SecretKey, input: EncryptedObject): Promise<PlainObject> {
    const decryptedObject: { [key: string]: PlainValue } = {};
    for (const key in input) {
        const value = input[key] as typeof input[typeof key];
        decryptedObject[key] = await decrypt(publicKey, secretKey, value);
    }
    return decryptedObject;
}

function decryptArray(publicKey: X25519PublicKey, secretKey: X25519SecretKey, input: EncryptedArray): Promise<PlainArray> {
    return Promise.all(input.map(x => decrypt(publicKey, secretKey, x)));
}

export async function decrypt(publicKey: X25519PublicKey, secretKey: X25519SecretKey, input: EncryptedString): Promise<PlainString>
export async function decrypt(publicKey: X25519PublicKey, secretKey: X25519SecretKey, input: EncryptedObject): Promise<PlainObject>
export async function decrypt(publicKey: X25519PublicKey, secretKey: X25519SecretKey, input: EncryptedArray): Promise<PlainArray>
export async function decrypt(publicKey: X25519PublicKey, secretKey: X25519SecretKey, input: EncryptedValue): Promise<PlainValue>
export async function decrypt(publicKey: X25519PublicKey, secretKey: X25519SecretKey, input: EncryptedValue): Promise<PlainValue> {
    switch (typeof input) {
        case "bigint":
        case "boolean":
        case "number":
            return input;

        case "string":
            return decryptString(publicKey, secretKey, input);

        case "object":
            if (!input) {
                return null;
            }

            if (Array.isArray(input)) {
                return decryptArray(publicKey, secretKey, input);
            }

            return decryptObject(publicKey, secretKey, input);

        case "function":
        case "symbol":
        case "undefined":
            throw new UnreachableVariantError(input);
    }
}